import t from "@/lang/lang";
import {
  PutRequest,
  useTextblocksCatalogPutMutation,
} from "@/services/backend/textblocks/api/catalog-api";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInput } from "@/shared/components/form/text-input";
import { TextareaInput } from "@/shared/components/form/textarea-input";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { Label } from "@/shared/components/ui/label";
import { NullUUID } from "@bitory-gmbh/nidavellir";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { v4 } from "uuid";

export function CreateCatalogDialog() {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<PutRequest>({
    id: v4(),
    companyId: NullUUID,
    name: "",
    description: "",
    active: true,
    items: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  const [put, { isLoading, error, isSuccess, reset }] =
    useTextblocksCatalogPutMutation();

  useEffect(() => {
    if (isSuccess) {
      reset();
      setOpen(false);
      setRequest({
        id: v4(),
        companyId: NullUUID,
        name: "",
        description: "",
        active: true,
        items: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
  }, [isSuccess, reset, setOpen]);

  const doCreate = () => {
    if (isLoading) return;

    put(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus />
          <span>{t("Katalog")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Neuen Bemerkungskatalog erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Erstellen Sie einen neuen Katalog, um dienstleitungsspezifische Bemerkungen zu verwalten.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label>{t("Unternehmen")}</Label>
            <CompanySelect
              companyId={request.companyId}
              onCompanyIdChange={(companyId) =>
                setRequest({ ...request, companyId })
              }
            />
            <InputValidationErrors error={error} field="companyId" />
          </div>
          <TextInput
            label={t("Name")}
            field="name"
            text={request.name}
            onTextChange={(name) => setRequest({ ...request, name })}
            errors={inputValidationErrorMessages(error, "name")}
          />
          <TextareaInput
            label={t("Beschreibung")}
            field="description"
            text={request.description}
            onTextChange={(description) =>
              setRequest({ ...request, description })
            }
            errors={inputValidationErrorMessages(error, "description")}
          />
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter isLoading={false} onClickAction={doCreate} />
      </DialogContent>
    </Dialog>
  );
}
